import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Avatar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemButton,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import logoSrc from "@arelia-assets/logo-invert.png";
import { PATHS } from "../../routes";
import { Account } from "../../types";
import AppContext from "../../AppContext";
import { useContext, useMemo } from "react";
import { uniqBy } from "lodash";
import MenuSelector from "../ui-atoms/menu-selector/MenuSelector";
import { useConfiguration } from "../../api/configuration";
import { areliaRoutes, hqRoutes } from "./constant";
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';

export default function Navigation() {
  const { account, connections, selectedArtist, setSelectedArtist, selectHqIsActive } = useContext(AppContext);

  const { data: config } = useConfiguration();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const changePage = (link: string) => () => !pathname.startsWith(link) && navigate(link);
  const artistAccounts: any[] = useMemo(() => {
    const accountNames = uniqBy(connections?.filter(x => x.connected).flatMap(x => x.accounts) ?? [], x => x.name?.toUpperCase()).map(x => x.name);
    return [{ value: null, label: "All Artists" }, ...accountNames.map(x => ({ value: x, label: x }))];
  }, [connections]);

  const getName = (acc: Account | null) => {
    if (!acc) return "";
    if (acc?.fullName) return acc?.fullName;
    return acc?.email.split("@")[0].replace(".", " ");
  }
  return (
    <Box
      display="flex"
      sx={{
        flexDirection: "column",
        width: 250,
        minWidth: 250,
        height: "100vh",
        bgcolor: "#000",
        color: "info.main",
        pl: 5,
        pr: 1,
        pt: 5,
      }}
    >
      <Box display="flex" gap={1} alignItems="center" mb={3}>
        <img src={logoSrc} alt="avatar" />
        {selectHqIsActive  && <Typography sx={{
          backgroundColor: "#23BB9D",
          padding: "2px 8px",
          borderRadius: "30px",
          color: "#fff",
          fontSize: "10px",
          fontWeight: 500
        }}>
          HQ
        </Typography>}
      </Box>
      <List>
        <ListItemButton
          sx={{ pl: 0 }}
          onClick={changePage(PATHS.ACCOUNT)}
        >
          <ListItemAvatar>
            <Avatar src={account?.avatarUrl} />
          </ListItemAvatar>
          <ListItemText primary={getName(account)} primaryTypographyProps={{ color: "#F7F8FA" }} />
          <ListItemSecondaryAction>
            <KeyboardArrowRightOutlined />
          </ListItemSecondaryAction>
        </ListItemButton>
      </List>

      {config?.flags.EnableArtistSelector && artistAccounts.length > 2 ? <Box mr={3} mt={3}>
        <MenuSelector menuItems={artistAccounts} selectedValue={selectedArtist} onClick={setSelectedArtist} />
      </Box> : <></>}

      <List sx={{ mt: 5 }}>
        {(selectHqIsActive ? hqRoutes : areliaRoutes).map(({ id, link, label, icon: IconComponent }) => (
          <ListItemButton
            key={id}
            selected={pathname.startsWith(link)}
            onClick={changePage(link)}
            sx={{
              pl: 0,
              "& .MuiListItemText-primary": {
                color: "primary.main",
              },
              "& .MuiListItemIcon-root": {
                color: "primary.main",
              },
              "&:hover .MuiListItemText-primary, &.Mui-selected .MuiListItemText-primary":
              {
                color: "#fff",
              },
              "&:hover .MuiListItemIcon-root, &.Mui-selected .MuiListItemIcon-root":
              {
                color: "#00bcd4",
              },
              "&.Mui-selected, &.Mui-selected:hover": {
                bgcolor: "transparent",
              },
              "&:hover": {
                bgcolor: "transparent",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <IconComponent />
            </ListItemIcon>
            <ListItemText primary={label} />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ mt: "auto", mb: 2, color: "#F7F8FA", display: "flex", justifyContent: "center", flexDirection: "column", marginLeft: "-40px", gap: "6px" }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px" }}>
          <ContactSupportOutlinedIcon />
          <a href="https://areliatech.com/arelia-university" target="_blank" rel="noopener noreferrer" style={{ color: "#F7F8FA" }}>
            ARELIA University
          </a>
        </Box>
        <Typography sx={{ textAlign: "center" }}>Version {config?.version}</Typography>
      </Box>
    </Box>
  );
}
